import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import type { LabRunData } from "@/lib/labModel";
import type { LabRunModel } from "@/lib/labRunModel";
import { LAB_SCROLL_PANE, paneTabs } from "@/lib/labLayout";
import {
  LAB_PANES,
  LAB_PANE_HELP,
  LAB_PANE_LABELS,
  LAB_RUN_TAB_LABELS,
  type LabPaneId,
  type LabRunTabId,
  type LabTabId,
  type LabTurnTabId,
} from "@/lib/labUrl";
import { Tooltip } from "@/ui/Tooltip";
import { DeniedState, EmptyState, ErrorState, LoadingState } from "@/ui/states";

import { LabPanelToggle } from "./LabPanelToggle";
import { LabRunSummary } from "./LabRunSummary";
import { LabTurnInspector } from "./LabTurnInspector";
import { LabClassificationStateTab } from "./turn/LabClassificationStateTab";
import { LabCompareTab } from "./run/LabCompareTab";
import { LabFlowGraphTab } from "./run/LabFlowGraphTab";
import { LabLatencyTab } from "./run/LabLatencyTab";
import { LabMetricsTab } from "./run/LabMetricsTab";
import { LabOverviewTab } from "./run/LabOverviewTab";
import { LabPromptsTab } from "./run/LabPromptsTab";
import { LabRawEventsTab } from "./run/LabRawEventsTab";
import { LabSafetyRunTab } from "./run/LabSafetyRunTab";
import { LabRunServicesTab } from "./run/LabServicesTab";
import { LabTimelineTab } from "./run/LabTimelineTab";
import { LabTabStrip } from "./turn/LabTabStrip";

const RUN_STRIP_ID = "lab-run";
const RUN_PANEL_ID = `${RUN_STRIP_ID}-panel`;

/**
 * What a run view shows until its events are readable. Without it a view built
 * from a run whose events are still loading reads as an empty run ("No turns
 * yet"). Null once the run is ready, so the view itself renders.
 */
function runNotReady(data: LabRunData | null, label: string): ReactNode {
  switch (data?.contentState) {
    case undefined:
    case "ready":
      return null;
    case "pending":
      return <LoadingState label={`Loading ${label}`} rows={3} />;
    case "denied":
      return (
        <DeniedState
          title="This preview run is not available to your account"
          description="Your account is not authorized to inspect preview runs in this project in this environment. Nothing about the run has been read."
        />
      );
    case "error":
      return (
        <ErrorState
          title="This preview run could not be read"
          onRetry={data.refetch}
        />
      );
    case "unavailable":
      return (
        <EmptyState
          title="Nothing recorded yet"
          description="This view fills in once the runtime records events for the run."
        />
      );
  }
}

function Hideable({
  hidden,
  children,
}: {
  hidden: boolean;
  children: ReactNode;
}) {
  return <div hidden={hidden}>{children}</div>;
}

/**
 * The Lab's right pane: a "This turn | Whole run" switch over the turn
 * inspector and the whole-run views. "Expand" opens the current view wide over
 * the centre (Esc or Collapse closes it). At phone width it is a bottom panel
 * with a handle.
 */
export function LabInspectorPane({
  projectId,
  primary,
  compare,
  primaryModel,
  compareModel,
  selectedTurn,
  pane,
  tab,
  compareOn,
  onPaneChange,
  onTabChange,
  onSelectTurn,
  onOpenRuns,
  sheetOpen,
  onSheetOpenChange,
}: {
  projectId: string;
  primary: LabRunData | null;
  compare: LabRunData | null;
  primaryModel: LabRunModel | null;
  compareModel: LabRunModel | null;
  selectedTurn: number | null;
  pane: LabPaneId;
  /** The selected tab of `pane` (already resolved to one the pane offers). */
  tab: LabTabId;
  compareOn: boolean;
  onPaneChange: (pane: LabPaneId) => void;
  onTabChange: (tab: LabTabId) => void;
  /** A turn picked inside the pane or its views: selects it, keeps the pane. */
  onSelectTurn: (turn: number) => void;
  /** Opens a run with a second one beside it (the A/B result). */
  onOpenRuns: (runRef: string, compareRef: string) => void;
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpanded(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expanded]);

  const runTab = pane === "run" ? (tab as LabRunTabId) : null;
  // Run panels mount on first view and then stay (hidden), so loaded text and
  // scroll survive a tab switch.
  const [visited, setVisited] = useState<ReadonlySet<LabRunTabId>>(
    () => new Set<LabRunTabId>(),
  );
  if (runTab !== null && !visited.has(runTab)) {
    setVisited(new Set([...visited, runTab]));
  }
  const runKey = primary?.previewRunRef ?? "none";
  const tabs = paneTabs(pane, compareOn);

  const runPanel = (id: LabRunTabId) => {
    if (id !== runTab && !visited.has(id)) {
      return null;
    }
    if (id === "compare" && !compareOn) {
      return null;
    }
    const model = primaryModel;
    const common = {
      projectId,
      selectedTurn,
      onSelectTurn,
    };
    let body: ReactNode;
    const notReady = runNotReady(primary, LAB_RUN_TAB_LABELS[id]);
    if (id === "services" && primary !== null) {
      // Reads no trace, so it does not wait for the run's events.
      body = (
        <LabRunServicesTab
          projectId={projectId}
          previewRunRef={primary.previewRunRef}
          live={primary.live}
          onSelectTurn={onSelectTurn}
          onOpenRuns={onOpenRuns}
        />
      );
    } else if (id === "state" && primary !== null && notReady === null) {
      body = (
        <LabClassificationStateTab
          projectId={projectId}
          run={primary}
          throughTurn={null}
        />
      );
    } else if (model === null) {
      body = (
        <EmptyState
          title="No active conversation"
          description="Choose the settings above and press New conversation, or open one from History."
        />
      );
    } else if (notReady !== null) {
      body = notReady;
    } else if (id === "overview") {
      body = (
        <div className="space-y-4">
          {primary !== null ? (
            <p className="text-xs text-muted-foreground">
              Run{" "}
              <span dir="ltr" className="font-mono">
                {primary.previewRunRef}
              </span>
            </p>
          ) : null}
          <LabOverviewTab {...common} model={model} />
          {primary !== null ? (
            <LabRunSummary
              defaultOpen
              runs={[
                { label: "Run A", run: primary },
                ...(compare !== null ? [{ label: "Run B", run: compare }] : []),
              ]}
            />
          ) : null}
        </div>
      );
    } else if (id === "safety") {
      body = (
        <LabSafetyRunTab
          {...common}
          model={model}
          live={primary?.live ?? false}
        />
      );
    } else if (id === "timeline") {
      body = <LabTimelineTab {...common} model={model} />;
    } else if (id === "flow") {
      body = <LabFlowGraphTab {...common} model={model} />;
    } else if (id === "prompts") {
      body = <LabPromptsTab {...common} model={model} />;
    } else if (id === "latency") {
      body = <LabLatencyTab {...common} model={model} />;
    } else if (id === "raw") {
      body = <LabRawEventsTab {...common} model={model} />;
    } else if (id === "metrics") {
      body = <LabMetricsTab {...common} model={model} />;
    } else {
      body = runNotReady(compare, "second run") ?? (
        <LabCompareTab {...common} primary={model} compare={compareModel} />
      );
    }
    return (
      <Hideable key={`${id}-${runKey}`} hidden={runTab !== id}>
        {body}
      </Hideable>
    );
  };

  return (
    <aside
      aria-label="Inspector"
      className={cn(
        "flex min-h-0 min-w-0 flex-col bg-background",
        expanded &&
          "min-[900px]:absolute min-[900px]:inset-0 min-[900px]:z-20 min-[900px]:border-s min-[900px]:border-border",
        "max-[899px]:fixed max-[899px]:inset-x-0 max-[899px]:bottom-0 max-[899px]:z-30 max-[899px]:border-t max-[899px]:border-border max-[899px]:bg-card max-[899px]:shadow-lg",
      )}
    >
      <button
        type="button"
        aria-expanded={sheetOpen}
        aria-controls="lab-inspector-body"
        onClick={() => onSheetOpenChange(!sheetOpen)}
        className="flex h-10 w-full items-center justify-center gap-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring min-[900px]:hidden"
      >
        <span aria-hidden="true" className="h-1 w-8 rounded-full bg-border" />
        {sheetOpen ? "Hide inspector" : "Show inspector"}
      </button>
      <div
        id="lab-inspector-body"
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          !sheetOpen && "max-[899px]:hidden",
          sheetOpen && "max-[899px]:max-h-[65vh]",
        )}
      >
        <div className="flex min-h-12 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-1.5">
          <div
            role="group"
            aria-label="Inspector scope"
            className="inline-flex rounded-full border border-border bg-secondary p-0.5"
          >
            {LAB_PANES.map((id) => (
              <Tooltip key={id} content={LAB_PANE_HELP[id]}>
                <button
                  type="button"
                  aria-pressed={pane === id}
                  onClick={() => onPaneChange(id)}
                  className={cn(
                    "rounded-full px-3 py-1 text-sm focus-visible:outline-2 focus-visible:outline-ring",
                    pane === id
                      ? "bg-primary font-medium text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {LAB_PANE_LABELS[id]}
                </button>
              </Tooltip>
            ))}
          </div>
          <LabPanelToggle
            label={expanded ? "Collapse" : "Expand"}
            icon={expanded ? PanelRightClose : PanelRightOpen}
            pressed={expanded}
            onClick={() => setExpanded(!expanded)}
            className="max-[899px]:hidden"
          />
        </div>

        <div className={cn("flex-1 p-4 pt-0", LAB_SCROLL_PANE)}>
          {pane === "run" ? (
            <div className="sticky top-0 z-10 -mx-4 mb-3 bg-background px-4 pt-3">
              <LabTabStrip
                idPrefix={RUN_STRIP_ID}
                ariaLabel="Run views"
                tabs={
                  tabs as { id: LabRunTabId; label: string; help: string }[]
                }
                value={tab as LabRunTabId}
                onChange={onTabChange}
                panelId={RUN_PANEL_ID}
              />
            </div>
          ) : (
            <div className="h-3" />
          )}
          {/* Both scopes stay mounted (one hidden), so a switch loses nothing. */}
          <div hidden={pane !== "turn"}>
            <LabTurnInspector
              projectId={projectId}
              primary={primary}
              compare={compare}
              selectedTurn={selectedTurn}
              itab={(pane === "turn" ? tab : "summary") as LabTurnTabId}
              onItabChange={onTabChange}
              model={primaryModel}
              compareModel={compareModel}
              onSelectTurn={onSelectTurn}
            />
          </div>
          <div
            hidden={pane !== "run"}
            role="tabpanel"
            id={RUN_PANEL_ID}
            aria-labelledby={`${RUN_STRIP_ID}-${tab}`}
            className="min-w-0"
          >
            {(
              [
                "overview",
                "state",
                "timeline",
                "flow",
                "prompts",
                "safety",
                "latency",
                "metrics",
                "services",
                "raw",
                "compare",
              ] as const
            ).map(runPanel)}
          </div>
        </div>
      </div>
    </aside>
  );
}
